import { useCallback, useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { AlertTriangle, Inbox, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { PlatformAdminOnly } from "@/components/portal/PlatformAdminOnly";
import {
  platformApi,
  DEAD_LETTER_PAGE_LIMIT,
  type InboundDeadLetter,
  type InboundDeadLetterDetail,
} from "@/services/api/platform";

export const Route = createFileRoute("/portal/platform/inbound-dead-letters")({
  component: InboundDeadLettersRoute,
});

function InboundDeadLettersRoute() {
  return (
    <PlatformAdminOnly backTo="/portal/platform" backLabel="Back to platform">
      <InboundDeadLettersPage />
    </PlatformAdminOnly>
  );
}

/** "3 days ago" style age — the oldest lost message is the most urgent. */
function ageLabel(createdAtUtc: string | null): string {
  if (!createdAtUtc) return "unknown";
  const created = new Date(
    createdAtUtc.endsWith("Z") ? createdAtUtc : `${createdAtUtc}Z`,
  ).getTime();
  if (Number.isNaN(created)) return "unknown";
  const minutes = Math.max(0, Math.floor((Date.now() - created) / 60000));
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 1440) return `${Math.floor(minutes / 60)}h`;
  return `${Math.floor(minutes / 1440)}d`;
}

/** First line only — the stored Error is an exception plus a full traceback. */
function errorSummary(error: string | null): string {
  // Empty as well as null: `str(e)` is "" for a bare Exception, and the parked
  // Error is f"{e}\n{traceback}" — so a blank first line is a real case, and it
  // would otherwise render the payload button with no label at all.
  if (!error?.trim()) return "(no error recorded)";
  const firstLine = error.split("\n")[0].trim() || "(no error message)";
  return firstLine.length > 140 ? `${firstLine.slice(0, 140)}…` : firstLine;
}

export function InboundDeadLettersPage() {
  const { token } = useAuth();

  const [rows, setRows] = useState<InboundDeadLetter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [replayTarget, setReplayTarget] = useState<InboundDeadLetter | null>(null);
  const [resolveTarget, setResolveTarget] = useState<InboundDeadLetter | null>(null);
  const [detail, setDetail] = useState<InboundDeadLetterDetail | null>(null);
  // id -> name. The messaging DB has no org table, so the dead letter carries
  // only a UUID; an operator triaging "whose message is this" needs the name.
  const [orgNames, setOrgNames] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      setRows(await platformApi.listInboundDeadLetters(token));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load dead letters");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!token) return;
    // Best-effort: a missing name degrades to the UUID rather than blocking
    // triage of a lost message.
    void platformApi
      .listOrganisations(token, { limit: 500 })
      .then((res) =>
        setOrgNames(
          Object.fromEntries(res.items.map((o) => [o.id.toLowerCase(), o.name])),
        ),
      )
      .catch(() => undefined);
  }, [token]);

  const handleReplay = useCallback(async () => {
    if (!token || !replayTarget) return;
    const target = replayTarget;
    setBusyId(target.id);
    setReplayTarget(null);
    try {
      const result = await platformApi.replayInboundDeadLetter(token, target.id);
      if (result.replayed) {
        toast.success("Message replayed and resolved.");
      } else {
        // Not a failure: the row was already resolved, or another operator is
        // replaying it. Surfaced rather than swallowed so the operator knows
        // their click did not do the work.
        toast.info(result.detail);
      }
      await load();
    } catch (e) {
      // The API returns 502 and leaves the row parked when the replay itself
      // fails — the message is still lost, so say so plainly.
      toast.error(
        e instanceof Error
          ? `Replay failed — message is still parked. ${e.message}`
          : "Replay failed — message is still parked.",
      );
      await load();
    } finally {
      setBusyId(null);
    }
  }, [token, replayTarget, load]);

  const handleResolve = useCallback(async () => {
    if (!token || !resolveTarget) return;
    const target = resolveTarget;
    setBusyId(target.id);
    setResolveTarget(null);
    try {
      const result = await platformApi.resolveInboundDeadLetter(token, target.id);
      toast.success(result.detail);
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Resolve failed");
    } finally {
      setBusyId(null);
    }
  }, [token, resolveTarget, load]);

  const openDetail = useCallback(
    async (row: InboundDeadLetter) => {
      if (!token) return;
      try {
        setDetail(await platformApi.getInboundDeadLetter(token, row.id));
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Failed to load payload");
      }
    },
    [token],
  );

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Inbound Dead Letters
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Inbound messages that failed after the provider was already
            acknowledged. Each one is a customer message that is lost until it is
            replayed.
          </p>
        </div>
        <Button variant="outline" onClick={() => void load()} disabled={loading}>
          <RefreshCw className={loading ? "mr-2 h-4 w-4 animate-spin" : "mr-2 h-4 w-4"} />
          Refresh
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Inbox className="h-5 w-5" />
            Unresolved
            {rows.length > 0 && (
              <Badge variant="destructive">{rows.length}</Badge>
            )}
          </CardTitle>
          <CardDescription>
            Oldest first — the message that has been waiting longest is the one
            most likely to still matter to somebody.
          </CardDescription>
          {rows.length >= DEAD_LETTER_PAGE_LIMIT && (
            <p className="text-destructive mt-2 text-sm font-medium">
              Showing the first {DEAD_LETTER_PAGE_LIMIT} — there are more parked
              messages than this page can list. Resolve some and refresh.
            </p>
          )}
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-muted-foreground flex items-center gap-2 py-8 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : error ? (
            <div className="text-destructive flex items-center gap-2 py-8 text-sm">
              <AlertTriangle className="h-4 w-4" />
              {error}
            </div>
          ) : rows.length === 0 ? (
            <p className="text-muted-foreground py-8 text-sm">
              No parked inbound messages. Nothing has been lost.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Organisation</TableHead>
                  <TableHead>Channel</TableHead>
                  <TableHead>Age</TableHead>
                  <TableHead>Error</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      {orgNames[row.organisation_id.toLowerCase()] ? (
                        <span>{orgNames[row.organisation_id.toLowerCase()]}</span>
                      ) : (
                        <span className="font-mono text-xs">
                          {row.organisation_id}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>{row.channel}</TableCell>
                    <TableCell>{ageLabel(row.created_at_utc)}</TableCell>
                    <TableCell className="max-w-md">
                      <button
                        type="button"
                        className="text-left text-sm underline-offset-2 hover:underline"
                        onClick={() => void openDetail(row)}
                      >
                        {errorSummary(row.error)}
                      </button>
                    </TableCell>
                    <TableCell className="space-x-2 text-right">
                      <Button
                        size="sm"
                        disabled={busyId === row.id}
                        onClick={() => setReplayTarget(row)}
                      >
                        {busyId === row.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          "Replay"
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busyId === row.id}
                        onClick={() => setResolveTarget(row)}
                      >
                        Resolve
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Replay re-injects a real customer message into a tenant's inbox, so it
          is confirmed rather than fired on a single click. */}
      <AlertDialog
        open={replayTarget !== null}
        onOpenChange={(open) => !open && setReplayTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replay this message?</AlertDialogTitle>
            <AlertDialogDescription>
              This re-runs the original inbound processing and delivers the
              customer&apos;s message into the organisation&apos;s inbox. It is
              only marked resolved if the replay succeeds.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleReplay();
              }}
            >
              Replay
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Resolving WITHOUT replaying discards a customer's message for good, so
          the wording has to say that rather than sound like a tidy-up. */}
      <AlertDialog
        open={resolveTarget !== null}
        onOpenChange={(open) => !open && setResolveTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Resolve without replaying?</AlertDialogTitle>
            <AlertDialogDescription>
              The message will NOT be delivered. Use this only when it has been
              handled another way — the customer&apos;s message is otherwise
              discarded permanently.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleResolve();
              }}
            >
              Resolve without replaying
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={detail !== null} onOpenChange={(open) => !open && setDetail(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Parked payload</DialogTitle>
            <DialogDescription>
              The only surviving copy of this message.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] space-y-4 overflow-auto">
            <div>
              <p className="mb-1 text-xs font-medium uppercase">Error</p>
              <pre className="bg-muted rounded p-3 text-xs whitespace-pre-wrap">
                {detail?.error ?? "(none)"}
              </pre>
            </div>
            <div>
              <p className="mb-1 text-xs font-medium uppercase">Payload</p>
              <pre className="bg-muted rounded p-3 text-xs whitespace-pre-wrap">
                {JSON.stringify(detail?.payload, null, 2)}
              </pre>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
